import { useMemo } from "react";
import type { SWRConfiguration } from "swr";
import LoadVelocityProfileService from "services/LoadVelocityProfileService";
import useQuery from "hooks/useQuery";
import type { Variants } from "types";
import type { LoadVelocityProfile } from "types/load-velocity-profile";
import ErrorLoggingService from "services/ErrorLoggingService";

export const getLatestLoadVelocityProfilesCacheKey = (
  athleteId: string,
  exerciseId: string,
  variants: Variants[] = []
) => ["LoadVelocityProfileService.getLatest", athleteId, exerciseId, ...variants] as const;

interface Options extends SWRConfiguration {
  enabled?: boolean;
}

interface GetLatestLoadVelocityProfilesQueryArgs {
  athleteId?: string | null;
  exerciseId?: string | null;
  /** Every variant is requested at once; without variants only the bilateral profile is returned. */
  variants?: Variants[];
}

interface GetLatestLoadVelocityProfiles {
  athleteId: string;
  exerciseId: string;
  variants: Variants[];
}

async function getLatestLoadVelocityProfiles(args: GetLatestLoadVelocityProfiles): Promise<LoadVelocityProfile[]> {
  const { athleteId, exerciseId, variants } = args;

  try {
    return await LoadVelocityProfileService.getLatest(athleteId, exerciseId, {
      variants: variants.length ? variants : undefined,
    });
  } catch (error) {
    ErrorLoggingService.captureException(error, "Failed to fetch latest load velocity profiles");
    throw error;
  }
}

const EMPTY_FALLBACK_DATA: LoadVelocityProfile[] = [];

/**
 * Fetches the latest load velocity profile of an athlete for an exercise: one per variant, or a single one when there are no variants.
 *
 * Variants without a profile are left out.
 */
function useGetLatestLoadVelocityProfilesQuery(args: GetLatestLoadVelocityProfilesQueryArgs, options?: Options) {
  const { athleteId, exerciseId, variants } = args;
  const { enabled = true, ...restOptions } = options || {};

  const key = useMemo(() => {
    if (!enabled || !athleteId || !exerciseId) {
      return null;
    }
    return getLatestLoadVelocityProfilesCacheKey(athleteId, exerciseId, variants);
  }, [athleteId, exerciseId, enabled, variants]);

  return useQuery(
    key,
    ([_, athleteId, exerciseId, ...variants]) => getLatestLoadVelocityProfiles({ athleteId, exerciseId, variants }),
    {
      ...restOptions,
      // Disable automatic retries for errors
      errorRetryCount: 0,
      fallbackData: EMPTY_FALLBACK_DATA,
    }
  );
}

export default useGetLatestLoadVelocityProfilesQuery;
